import { createButtonTokens, createModalTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Confirm Dialog component configuration using PrimeIcons
 *
 * ICON MAPPING:
 * - Info: pi-info-circle
 * - Error: pi-times-circle
 * - Success: pi-check-circle
 * - Warning: pi-exclamation-triangle
 */
export function createConfirmdialog(s: SemanticTokens) {
  const buttonTokens = createButtonTokens(s);
  const modalTokens = createModalTokens(s);
  return {
    // Inherits dialog tokens via PrimeNG extend
    icon: {
      size: modalTokens.closeBtn.size,
      color: modalTokens.fg,
    },
    content: {
      gap: modalTokens.content.gap,
    },
    css: () => `
    .p-confirmdialog {
      border-radius: ${modalTokens.base.radius};
      width: ${modalTokens.base.width};
    }
    
    .p-confirmdialog .p-dialog-header {
      padding: ${modalTokens.base.paddingY} ${modalTokens.base.paddingX} ${modalTokens.base.gap} ${modalTokens.base.paddingX};
      display: flex;
      align-items: center;
      gap: ${buttonTokens.base.gap};
      border-bottom: none;
      background: ${modalTokens.dialogBg};
      border-radius: ${modalTokens.base.radius} ${modalTokens.base.radius} 0 0;
    }
    
    /* Move icon from content to header - using :root for specificity */
    :root .p-confirmdialog .p-dialog-content .p-confirmdialog-icon {
      display: none !important;
    }
    
    /* Show icon in header using PrimeIcons — hidden by default, shown only when a known icon class is present */
    .p-confirmdialog .p-dialog-header::before {
      font-family: 'primeicons';
      font-size: ${modalTokens.icon.size};
      line-height: 1;
      display: none;
      align-self: center;
    }
    
    /* Info icon - pi-info-circle (\\e924) */
    .p-confirmdialog:has(.p-confirmdialog-icon.pi-info-circle) .p-dialog-header::before {
      content: '\\e924';
      color: ${modalTokens.fg};
      display: inline-block;
    }
    
    /* Error icon - pi-times-circle (\\e90c) */
    .p-confirmdialog:has(.p-confirmdialog-icon.pi-times-circle) .p-dialog-header::before {
      content: '\\e90c';
      color: ${modalTokens.statusIcon.danger};
      display: inline-block;
    }
    
    /* Success icon - pi-check-circle (\\e90a) */
    .p-confirmdialog:has(.p-confirmdialog-icon.pi-check-circle) .p-dialog-header::before {
      content: '\\e90a';
      color: ${modalTokens.statusIcon.success};
      display: inline-block;
    }
    
    /* Warning icon - pi-exclamation-triangle (\\e922) */
    .p-confirmdialog:has(.p-confirmdialog-icon.pi-exclamation-triangle) .p-dialog-header::before {
      content: '\\e922';
      color: ${modalTokens.statusIcon.warning};
      display: inline-block;
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-title {
      font-size: ${modalTokens.title.fontSize};
      font-weight: ${modalTokens.title.fontWeight};
      color: ${modalTokens.header.headerFg};
      line-height: ${modalTokens.title.lineHeight};
      margin: 0;
      flex: 1;
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions {
      display: flex;
      align-items: center;
      gap: ${modalTokens.footer.gap};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button {
      height: ${modalTokens.closeBtn.size};
      width: ${modalTokens.closeBtn.size};
      min-width: ${modalTokens.closeBtn.size};
      min-height: ${modalTokens.closeBtn.size};
      padding: ${modalTokens.closeBtn.padding};
      border-radius: ${modalTokens.closeBtn.radius};
      border: none !important;
      background: ${modalTokens.closeBtn.bg};
      color: ${modalTokens.closeBtn.fg};
      font-weight: ${modalTokens.closeBtn.fontWeight};
      font-size: ${modalTokens.closeBtn.fontSize};
      transition: background-color ${modalTokens.closeBtn.transition}, color ${modalTokens.closeBtn.transition};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button .p-icon { 
      width: ${modalTokens.closeBtn.iconSize}; 
      height: ${modalTokens.closeBtn.iconSize};
      margin: 0;
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):hover { 
      background: ${modalTokens.closeBtn.bgHover}; 
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):hover .p-icon {
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):active {
      background: ${modalTokens.closeBtn.bgActive};
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):active .p-icon {
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button:disabled,
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button.p-disabled {
      background: ${modalTokens.closeBtn.bgDisabled};
      color: ${modalTokens.closeBtn.fgDisabled};
    }
    
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button:disabled .p-icon,
    .p-confirmdialog .p-dialog-header .p-dialog-header-actions .p-button.p-disabled .p-icon {
      color: ${modalTokens.closeBtn.fgDisabled};
    }
    
    .p-confirmdialog .p-dialog-content { 
      padding: 0 ${modalTokens.base.paddingX} ${modalTokens.base.paddingX} ${modalTokens.base.paddingX}; 
    }

    /* When message is empty, remove bottom padding from header */
    .p-confirmdialog:has(.p-confirmdialog-message:empty) .p-dialog-header {
      padding-bottom: 0;
    }
    
    .p-confirmdialog .p-dialog-content .p-confirmdialog-message {
      font-size: ${modalTokens.content.fontSize};
      color: ${modalTokens.fg};
      line-height: ${modalTokens.content.lineHeight};
    }

    .p-confirmdialog .p-dialog-content .p-confirmdialog-message p {
      margin: 0;
    }

    .p-confirmdialog .p-dialog-content .p-confirmdialog-message ul {
      margin: 1rem 0 0 0;
      padding: 0;
      list-style: none;
    }
    
    .p-confirmdialog .p-dialog-footer {
      padding: 0 ${modalTokens.base.paddingX} ${modalTokens.base.paddingX} ${modalTokens.base.paddingX};
      display: flex;
      gap: ${modalTokens.footer.gap};
      justify-content: space-between;
      border-top: none;
    }

    /* Hide footer entirely when both buttons are hidden (e.g. rejectVisible=false + acceptVisible=false) */
    .p-confirmdialog:not(:has(.p-confirmdialog-accept-button, .p-confirmdialog-reject-button)) .p-dialog-footer {
      display: none;
    }
    
    /* Cancel/Reject button - Contrast variant */
    .p-confirmdialog .p-dialog-footer .p-confirmdialog-reject-button {
      background: ${buttonTokens.ghost.bg};
      border: 1px solid ${buttonTokens.ghost.border};
      color: ${buttonTokens.ghost.fg};
      padding: ${buttonTokens.base.paddingX};
      border-radius: ${buttonTokens.base.radius};
      font-weight: ${modalTokens.footer.fontWeight};
      font-size: ${modalTokens.footer.fontSize};
      line-height: ${modalTokens.footer.lineHeight};
    }
    
    .p-confirmdialog .p-dialog-footer .p-confirmdialog-reject-button:hover {
      background: ${buttonTokens.ghost.bgHover};
      color: ${buttonTokens.ghost.fg};
      border-color: ${buttonTokens.ghost.border};
    }
    
    .p-confirmdialog .p-dialog-footer .p-confirmdialog-reject-button:active {
      background: ${buttonTokens.ghost.bgPressed};
      color: ${buttonTokens.ghost.fg};
      border-color: ${buttonTokens.ghost.border};
    }
    
    /* Default Accept button - Secondary style */
    .p-confirmdialog .p-dialog-footer .p-confirmdialog-accept-button {
      background: ${buttonTokens.secondary.bg};
      border: none;
      color: ${buttonTokens.secondary.fg};
      padding: ${buttonTokens.base.paddingX};
      border-radius: ${buttonTokens.base.radius};
      font-weight: ${modalTokens.footer.fontWeight};
      font-size: ${modalTokens.footer.fontSize};
      line-height: ${modalTokens.footer.lineHeight};
    }
    
    .p-confirmdialog .p-dialog-footer .p-confirmdialog-accept-button:hover {
      border: none !important;
      background: ${buttonTokens.secondary.bgHover};
    }
    
    /* Status button variants */
    .p-confirmdialog .p-dialog-footer .p-button-danger {
      background: ${buttonTokens.status.danger.bg};
      border: none;
      color: ${buttonTokens.status.danger.fg};
    }
    
    .p-confirmdialog .p-dialog-footer .p-button-danger:hover {
      background: ${buttonTokens.status.danger.bgHover};
    }
    
    .p-confirmdialog .p-dialog-footer .p-button-success {
      background: ${buttonTokens.status.success.bg};
      border: none;
      color: ${buttonTokens.status.success.fg};
    }
    
    .p-confirmdialog .p-dialog-footer .p-button-success:hover {
      background: ${buttonTokens.status.success.bgHover};
    }
    
    .p-confirmdialog .p-dialog-footer .p-button-warn {
      background: ${buttonTokens.status.warning.bg};
      border: none;
      color: ${buttonTokens.status.warning.fg};
    }
    
    .p-confirmdialog .p-dialog-footer .p-button-warn:hover {
      background: ${buttonTokens.status.warning.bgHover};
    }
    
    .p-dialog-mask { 
      background-color: ${modalTokens.overlayBg}; 
    }
    
    .p-confirmdialog-reject-button:focus-visible,
    .p-confirmdialog-accept-button:focus-visible {
      outline: 2px solid ${modalTokens.focusRing};
      outline-offset: 2px;
      box-shadow: none;
    }

    .p-confirmdialog-reject-button:focus:not(:focus-visible),
    .p-confirmdialog-accept-button:focus:not(:focus-visible) {
      outline: none;
      box-shadow: none;
    }
  `,
  };
}
export const confirmdialog = createConfirmdialog(SEMANTIC);
